import { evaluateExpression, get, set } from "./Utils";

interface ValidationResult {
    [key: string]: string;
}

type ValidationIssue = {
    item: string;
    message: string;
}


interface Rule {
    type?: string;
    format?: string;
    required?: boolean;
    requiredIf?: Rule;
    minimum?: number;
    maximum?: number;
    exclusiveMinimum?: number;
    exclusiveMaximum?: number;
    positive?: boolean;
    multipleOf?: number;
    maxDecimalPlaces?: number;
    minLength?: number;
    maxLength?: number;
    pattern?: string;
    minItems?: number;
    maxItems?: number;
    properties?: { [key: string]: Rule }; // For complex types
    lessThan?: string; // To support comparisons with other fields
    greaterThan?: string; // To support comparisons with other fields
    equalTo?: string; // To support comparisons with other fields
    messages?: ValidationResult; // Custom messages
    severities?: ValidationResult; // Per-validation severity overrides
    severity?: "error" | "warning"; // Legacy whole-rule severity
    allOf?: Rule[];
    anyOf?: Rule[];
    oneOf?: Rule[];
    not?: Rule;
    const?: any;
    if?: Rule;
    then?: Rule;
    else?: Rule;
    sqlDbType?: string; // Additional property for SQL specifics
}

function isWarning(rule: Rule, item: string): boolean {
    return (rule.severities?.[item] ?? rule.severity)?.toLowerCase() === 'warning';
}

function addValidationMessage(errors: ValidationResult, warnings: ValidationResult | undefined, rule: Rule, item: string, key: string, message: string) {
    if (isWarning(rule, item)) {
        if (warnings) {
            warnings[key] = message;
        }
    } else {
        errors[key] = message;
    }
}

function makeIssue(item: string, message: string): ValidationIssue {
    return { item, message };
}
export function getPropertyRule(rule: Rule, dataKey: string): Rule | undefined {
    const propertyRule: Rule | undefined = rule?.type === "object" ? get(rule, `properties.${dataKey}`, undefined) : undefined;
    //console.log(dataKey);
    //console.log(rule);
    //console.log(propertyRule);
    if (propertyRule && propertyRule.type !== "object") {
        return propertyRule;
    }

    return undefined;
}

export function validate(data: any, rule: Rule, path: string = "", warnings?: ValidationResult): ValidationResult {
    const errors: { [key: string]: string } = {};

    //console.log("validate");
    //console.log(rule);
    //console.log(path);
    //console.log(data);

    // Required validation
    if (data === undefined || data === null || data === "") {
        if (rule.required === true || rule.const !== undefined) {
            addValidationMessage(errors, warnings, rule, "required", path, rule.messages?.required || `${path} is required.`);
        }
        return errors; // Stop further checks if we have no such prop specified
    }
    else {
        if (rule.required === false) {
            addValidationMessage(errors, warnings, rule, "required", path, rule.messages?.required || `${path} is not required.`);
        }
    }

    // Check 'not' condition
    if (rule.not) {
        const notError = validate(data, rule.not, path, warnings);
        if (!notError) {
            addValidationMessage(errors, warnings, rule, "not", path, rule.messages?.not || `${path} must not conform to the 'not' condition.`);
        }
    }

    // Basic type and constraint validations
    if (rule.type === "number" || rule.type === "integer") {
        const numError = validateNumeric(data, rule, path);
        if (numError) addValidationMessage(errors, warnings, rule, numError.item, path, numError.message);
    } else if (rule.type === "string") {
        const strError = validateString(data, rule, path);
        if (strError) addValidationMessage(errors, warnings, rule, strError.item, path, strError.message);
    } else if (rule.type === "array") {
        const arrError = validateArray(data, rule, path);
        if (arrError) addValidationMessage(errors, warnings, rule, arrError.item, path, arrError.message);
    }

    // Handle allOf, anyOf, oneOf, not, const
    if (rule.allOf) {
        const allOfErrors: { [key: string]: string } = {};
        for (const subRule of rule.allOf) {
            const subErrors = validate(data, subRule, path, warnings);
            Object.assign(allOfErrors, subErrors);
        }
        Object.assign(errors, allOfErrors); // Aggregate all errors from allOf
    } else if (rule.anyOf) {
        let anyOfPassed = false;
        for (const subRule of rule.anyOf) {
            const subErrors = validate(data, subRule, path, warnings);
            if (Object.keys(subErrors).length === 0) {
                anyOfPassed = true;
                break; // If one passes, no need to check others
            }
        }
        if (!anyOfPassed) {
            addValidationMessage(errors, warnings, rule, "anyOf", path, "None of the anyOf conditions were met.");
        }
    } else if (rule.oneOf) {
        let oneOfPassedCount = 0;
        let oneOfErrors: { [key: string]: string } = {};
        for (const subRule of rule.oneOf) {
            const subErrors = validate(data, subRule, path, warnings);
            if (Object.keys(subErrors).length === 0) {
                oneOfPassedCount++;
            }
            Object.assign(oneOfErrors, subErrors);
        }
        if (oneOfPassedCount !== 1) {
            addValidationMessage(errors, warnings, rule, "oneOf", path, "Exactly one oneOf condition must be met.");
        }
        Object.assign(errors, oneOfErrors);
    } else if (rule.not) {
        const notErrors = validate(data, rule.not, path, warnings);
        if (Object.keys(notErrors).length === 0) {
            addValidationMessage(errors, warnings, rule, "not", path, "The 'not' condition was unexpectedly met.");
        }
    } else if (rule.const !== undefined) {
        if (data !== rule.const) {
            addValidationMessage(errors, warnings, rule, "const", path, `Value must be equal to ${JSON.stringify(rule.const)}`);
        }
    }

    // Handle if-then-else
    if (rule.if && rule.then) {
        const ifErrors = validate(data, rule.if);
        if (Object.keys(ifErrors).length === 0) {
            // If condition is true, apply 'then' rules
            const thenErrors = validate(data, rule.then, path, warnings);
            Object.assign(errors, thenErrors);
        } else if (rule.else) {
            // If condition is false, apply 'else' rules
            const elseErrors = validate(data, rule.else, path, warnings);
            Object.assign(errors, elseErrors);
        }
    }

    if (rule.format) {
        // Date validation (if applicable)
        if (rule.format === "Date") {
            const dateValue = new Date(data);
            if (isNaN(dateValue.getTime())) {
                addValidationMessage(errors, warnings, rule, "format", path, "Invalid date format.");
            } else if (dateValue >= new Date()) {
                addValidationMessage(errors, warnings, rule, "future", path, rule.messages?.future || `${path} cannot be in the future.`);
            }
        }
        else if (rule.format === "UsPhoneNumber") {
            const formatError = validate(data, {
                type: "string",
                pattern: "^[\\(]{0,1}([0-9]){3}[\\)]{0,1}[ ]?([^0-1]){1}([0-9]){2}[ ]?[-]?[ ]?([0-9]){4}[ ]*((x){0,1}([0-9]){1,5}){0,1}$"
            }, path, warnings);
            if (Object.keys(formatError).length !== 0) {
                addValidationMessage(errors, warnings, rule, "format", path, rule.messages?.format || `${path} must be a valid us phone number.`);
            }
        }
        else if (rule.format === "Email") {
            const formatError = validate(data, {
                type: "string",
                pattern: "^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$"
            }, path, warnings);
            if (Object.keys(formatError).length !== 0) {
                addValidationMessage(errors, warnings, rule, "format", path, rule.messages?.format || `${path} must be a valid us email address.`);
            }
        }
        else if (rule.format === "TimeIn24Hours") {
            const formatError = validate(data, {
                type: "string",
                pattern: "^(([0-1]?[0-9])|([2][0-3])):([0-5]?[0-9])$"
            }, path, warnings);
            if (Object.keys(formatError).length !== 0) {
                addValidationMessage(errors, warnings, rule, "format", path, rule.messages?.format || `${path} must be a valid 24 hours time format.`);
            }
        }
        else if (rule.format === "EmailListBySemiColon") {
            const emails = data.split(";").map((email: string) => email.trim());
            emails.forEach((email: string) => {
                const formatError = validate(email, {
                    type: "string",
                    format: "Email"
                }, path, warnings);
                if (Object.keys(formatError).length !== 0) {
                    addValidationMessage(errors, warnings, rule, "format", path, rule.messages?.format || `${path} must be a valid email list.`);
                }
            });
        }
    }

    // Handle nested objects/arrays (recursive calls)
    if (rule.properties) {
        for (const key in rule.properties) {
            const value = data[key];
            const keyPath = path === "" ? key : `${path}.{key}`;
            const nestedRule = rule.properties[key];
            const nestedErrors = validate(value, nestedRule, keyPath, warnings);
            for (const errorKey in nestedErrors) {
                if (errors[errorKey] === undefined) {
                    errors[errorKey] = nestedErrors[errorKey];
                }
            }

            // Cross property validations

            // Comparison logic if defined
            if (nestedRule.lessThan) {
                const compareToValue = data[nestedRule.lessThan];
                if (compareToValue !== undefined && value >= compareToValue) {
                    addValidationMessage(errors, warnings, nestedRule, "lessThan", keyPath, nestedRule.messages?.lessThan || `${key} must be less than ${rule.lessThan}.`);
                }
            }

            if (nestedRule.greaterThan) {
                const compareToValue = data[nestedRule.greaterThan];
                if (compareToValue !== undefined && value > compareToValue) {
                    addValidationMessage(errors, warnings, nestedRule, "greaterThan", keyPath, nestedRule.messages?.greaterThan || `${key} must be greater than ${rule.greaterThan}.`);
                }
            }

            if (nestedRule.equalTo) {
                const compareToValue = data[nestedRule.equalTo];
                if (compareToValue !== undefined && value != compareToValue) {
                    addValidationMessage(errors, warnings, nestedRule, "equalTo", keyPath, nestedRule.messages?.equalTo || `${key} must be equal to ${rule.equalTo}.`);
                }
            }

            // Handle cross property if-then-else
            if (nestedRule.if && nestedRule.then) {
                const ifErrors = validate(data, nestedRule.if);
                if (Object.keys(ifErrors).length === 0) {
                    // If condition is true, apply 'then' rules
                    const thenErrors = validate(data, nestedRule.then, path, warnings);
                    Object.assign(errors, thenErrors);
                } else if (nestedRule.else) {
                    // If condition is false, apply 'else' rules
                    const elseErrors = validate(data, nestedRule.else, path, warnings);
                    Object.assign(errors, elseErrors);
                }
            }

            if (nestedRule.requiredIf) {
                const ifErrors = validate(data, nestedRule.requiredIf);
                if (Object.keys(ifErrors).length === 0) {
                    // If condition is true, apply 'then' rules
                    const thenErrors = validate(value, { ...nestedRule, required: true }, keyPath, warnings);
                    Object.assign(errors, thenErrors);
                }
            }
        }
    }

    return errors;
}

export function validateWithWarnings(data: any, rule: Rule, path: string = ""): { errors: ValidationResult; warnings: ValidationResult } {
    const warnings: ValidationResult = {};
    const errors = validate(data, rule, path, warnings);
    return { errors, warnings };
}

function hasLessDecimalPlacesThan(value: any, num: number): boolean {
    // Check if value is null or undefined
    if (value == null) {
        return false; // was IsNothing in VB
    }

    // Ensure value can be converted to a string
    const test = value.toString();
    if (!test) {
        return false; // was String.IsNullOrEmpty in VB
    }

    // Try parsing the value to a number
    const testNum = Number(test);
    if (isNaN(testNum)) {
        return false; // cannot parse to double
    }

    // If there's no decimal point, valid
    if (!test.includes(".")) {
        return true;
    }

    // Get the substring after the decimal point
    const decimalIndex = test.indexOf(".");
    if (test.length - decimalIndex - 1 > num) {
        return false; // has more decimal places than allowed
    }

    return true; // Passed all checks if here
}

function validateNumeric(value: any, rule: Rule, field: string): ValidationIssue | null {
    if (typeof value === "string") {
        value = rule.type === "number" ? parseFloat(value) : parseInt(value);

        if (isNaN(value)) {
            return makeIssue("type", `${field} must be a number.`);
        }
    }

    // Check if the value is a valid number
    if (rule.type === "number" && typeof value !== "number") return makeIssue("type", `${field} must be a number.`);

    // Check exclusiveMinimum
    if (rule.exclusiveMinimum !== undefined && value <= rule.exclusiveMinimum) {
        return makeIssue("exclusiveMinimum", rule.messages?.exclusiveMinimum || `${field} must be greater than ${rule.exclusiveMinimum}.`);
    }

    // Check minimum
    if (rule.minimum !== undefined && value < rule.minimum) {
        return makeIssue("minimum", rule.messages?.minimum || `${field} must be greater than or equal to ${rule.minimum}.`);
    }

    // Check exclusiveMaximum
    if (rule.exclusiveMaximum !== undefined && value >= rule.exclusiveMaximum) {
        return makeIssue("exclusiveMaximum", rule.messages?.exclusiveMaximum || `${field} must be less than ${rule.exclusiveMaximum}.`);
    }

    // Custom check for decimal places
    if (rule.maxDecimalPlaces !== undefined && !hasLessDecimalPlacesThan(value, rule.maxDecimalPlaces)) {
        return makeIssue("maxDecimalPlaces", rule.messages?.maxDecimalPlaces || `${field} must have at most ${rule.maxDecimalPlaces} decimal places.`);
    }

    // Check maximum
    if (rule.maximum !== undefined && value > rule.maximum) {
        return makeIssue("maximum", rule.messages?.maximum || `${field} must be less than or equal to ${rule.maximum}.`);
    }

    // Check multipleOf
    if (rule.multipleOf !== undefined && value % rule.multipleOf !== 0) return makeIssue("multipleOf", rule.messages?.multipleOf || `${field} must be a multiple of ${rule.multipleOf}.`);

    // Check for maxLength and minLength constraints
    const len = value.toString().length;
    if (rule.minLength !== undefined && len < rule.minLength) return makeIssue("minLength", rule.messages?.minLength || `${field} must have at least ${rule.minLength} characters.`);
    if (rule.maxLength !== undefined && len > rule.maxLength) return makeIssue("maxLength", rule.messages?.maxLength || `${field} must have no more than ${rule.maxLength} characters.`);

    return null; // No errors
}

function validateString(value: any, rule: Rule, field: string): ValidationIssue | null {
    // Check if the value is valid
    if (rule.type === "string" && typeof value !== "string") return makeIssue("type", `${field} must be a string.`);

    // Check for maxLength and minLength constraints
    if (rule.minLength !== undefined && value.length < rule.minLength) return makeIssue("minLength", rule.messages?.minLength || `${field} must have at least ${rule.minLength} characters.`);
    if (rule.maxLength !== undefined && value.length > rule.maxLength) return makeIssue("maxLength", rule.messages?.maxLength || `${field} must have no more than ${rule.maxLength} characters.`);

    // Check pattern
    if (rule.pattern !== undefined) {
        const regex = new RegExp(rule.pattern);
        if (!regex.test(value)) {
            return makeIssue("pattern", rule.messages?.pattern || `${field} must match the pattern ${rule.pattern}.`);
        }
    }

    return null; // No errors
}

function validateArray(value: any, rule: Rule, field: string): ValidationIssue | null {
    // Check if the value is an array
    if (!Array.isArray(value)) return makeIssue("type", `${field} must be an array.`);

    // Check for minItems and maxItems constraints
    if (rule.minItems !== undefined && value.length < rule.minItems) return makeIssue("minItems", rule.messages?.minItems || `${field} must have at least ${rule.minItems} items.`);
    if (rule.maxItems !== undefined && value.length > rule.maxItems) return makeIssue("maxItems", rule.messages?.maxItems || `${field} must have no more than ${rule.maxItems} items.`);

    return null; // No errors
}

function validateObject(value: any, rule: Rule, field: string): ValidationIssue | null {
    // Check if the value is an object
    if (value !== null && typeof value !== "object") return makeIssue("type", `${field} must be an object.`);

    // Check for required fields
    if (rule.required && Array.isArray(rule.required)) {
        for (const requiredField of rule.required) {
            if (value[requiredField] === undefined) {
                return makeIssue("required", rule.messages?.required || `${requiredField} is required in ${field}.`);
            }
        }
    }

    return null; // No errors
}

export type { Rule, ValidationResult };
